import { forwardRef, useContext, useEffect, useState } from "react";
import {
  EyeIcon,
  EyeSlashIcon,
  ExclamationCircleIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline";
import { LanguageContext } from "../../context/LanguageContext";

const PasswordInputPro = forwardRef(function PasswordInputPro(
  {
    label,
    name,
    value = "",
    onChange,
    onBlur,
    error,
    success,
    required = true,
    disabled = false,
    className = "",
    showStrength = true,
    autoComplete = "current-password",
    ...props
  },
  ref
) {
  const { t, lang } = useContext(LanguageContext);
  const isRTL = lang === "ar";
  const dir = isRTL ? "rtl" : "ltr";

  const [focused, setFocused] = useState(false);
  const [show, setShow] = useState(false);
  const [strength, setStrength] = useState(0);
  const [hasValue, setHasValue] = useState(false);

  /* =========================
      DETECT VALUE (AUTOFILL FIX)
  ========================= */
  useEffect(() => {
    if (value && value.toString().trim() !== "") {
      setHasValue(true);
    } else {
      setHasValue(false);
    }
  }, [value]);

  /* =========================
      PASSWORD STRENGTH
  ========================= */
  useEffect(() => {
    if (!value || !showStrength) {
      setStrength(0);
      return;
    }

    let score = 0;
    if (value.length >= 8) score += 30;
    if (/[A-Z]/.test(value)) score += 20;
    if (/[a-z]/.test(value)) score += 20;
    if (/\d/.test(value)) score += 20;
    if (/[^A-Za-z0-9]/.test(value)) score += 10;

    setStrength(Math.min(score, 100));
  }, [value, showStrength]);

  const strengthLabel =
    strength < 40
      ? t("auth.password_weak") || "Weak"
      : strength < 70
      ? t("auth.password_medium") || "Medium"
      : strength < 90
      ? t("auth.password_good") || "Good"
      : t("auth.password_strong") || "Strong";

  const strengthColor =
    strength < 40
      ? "bg-red-500"
      : strength < 70
      ? "bg-yellow-500"
      : strength < 90
      ? "bg-blue-500"
      : "bg-green-500";

  return (
    <div className={`w-full space-y-1 ${className}`} dir={dir}>
      <div className="relative">
        {/* INPUT */}
        <input
          ref={ref}
          id={name}
          name={name}
          type={show ? "text" : "password"}
          value={value}
          disabled={disabled}
          autoComplete={autoComplete}
          onChange={onChange}
          onFocus={() => setFocused(true)}
          onBlur={(e) => {
            setFocused(false);
            onBlur && onBlur(e);
          }}
          placeholder=" "
          aria-invalid={!!error}
          className={`
            peer w-full h-12 px-4 pt-5
            rounded-lg
            bg-white dark:bg-gray-900
            border
            text-sm
            text-gray-900 dark:text-gray-100
            transition
            focus:outline-none focus:ring-2
            disabled:opacity-50 disabled:cursor-not-allowed
            ${isRTL ? "text-right" : "text-left"}
            ${
              error
                ? "border-red-500 focus:ring-red-500/20"
                : success
                ? "border-green-500 focus:ring-green-500/20"
                : "border-gray-300 dark:border-gray-700 focus:border-blue-600 focus:ring-blue-600/20"
            }
          `}
          {...props}
        />

        {/* LABEL */}
        <label
          htmlFor={name}
          className={`
            absolute px-1
            text-sm
            transition-all pointer-events-none
            ${
              focused || hasValue
                ? "top-1 text-xs text-blue-600 dark:text-blue-400"
                : "top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400"
            }
            ${isRTL ? "right-3" : "left-3"}
          `}
        >
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>

        {/* SHOW / HIDE */}
        <button
          type="button"
          onClick={() => setShow((v) => !v)}
          className={`
            absolute top-1/2 -translate-y-1/2
            text-gray-400 hover:text-gray-600
            dark:text-gray-500 dark:hover:text-gray-300
            ${isRTL ? "left-4" : "right-4"}
          `}
        >
          {show ? (
            <EyeSlashIcon className="w-5 h-5" />
          ) : (
            <EyeIcon className="w-5 h-5" />
          )}
        </button>

        {/* STATUS ICON */}
        {error && (
          <ExclamationCircleIcon
            className={`absolute top-1/2 -translate-y-1/2 w-5 h-5 text-red-500 ${
              isRTL ? "left-10" : "right-10"
            }`}
          />
        )}

        {success && !error && (
          <CheckCircleIcon
            className={`absolute top-1/2 -translate-y-1/2 w-5 h-5 text-green-500 ${
              isRTL ? "left-10" : "right-10"
            }`}
          />
        )}
      </div>

      {/* ERROR */}
      {error && (
        <p className="text-xs text-red-600 dark:text-red-400 flex items-center gap-1">
          <ExclamationCircleIcon className="w-4 h-4" />
          {error}
        </p>
      )}

      {/* SUCCESS */}
      {!error && success && (
        <p className="text-xs text-green-600 dark:text-green-400">
          {success}
        </p>
      )}

      {/* PASSWORD STRENGTH */}
      {showStrength && hasValue && !error && (
        <div className="space-y-1">
          <div className="h-1.5 bg-gray-200 dark:bg-gray-800 rounded-full overflow-hidden">
            <div
              className={`h-full transition-all ${strengthColor}`}
              style={{ width: `${strength}%` }}
            />
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {strengthLabel}
          </p>
        </div>
      )}
    </div>
  );
});

export default PasswordInputPro;
